/*
 * FILE: unified_ui/src/lib/commands.ts
 * PURPOSE: Typed subject constants matching botCore NATS subjects, plus request/response
 *          interfaces for each command used by the SPA.
 * OWNS: CMD constant map, request/response type definitions.
 * EXPORTS: CMD, SearchPrepareRequest, SearchPrepareResponse, SearchSelectExpansionRequest,
 *          SearchSelectExpansionResponse, SearchStartRequest, SearchStartResponse,
 *          SearchStatusRequest, SearchStatusResponse, SearchFinalizeRequest,
 *          SearchFinalizeResponse, SearchCancelRequest, SearchCancelResponse,
 *          SearchHistoryRequest, SearchHistoryResponse, ManagerHelpRequest,
 *          ManagerHelpResponse, RatingRequest, RatingResponse,
 *          SearchRunHistoryItem, ExpansionCandidate, SearchExecutionSnapshot.
 */

export const CMD = {
  searchPrepare: "cmd.core.search.prepare",
  searchSelectExpansion: "cmd.core.search.select_expansion",
  searchStart: "cmd.core.search.start",
  searchStatus: "cmd.core.search.status.get",
  searchCancel: "cmd.core.search.cancel",
  searchFinalize: "cmd.core.search.finalize",
  searchHistory: "cmd.core.search.history.list",
  managerHelpSubmit: "cmd.core.manager_help.submit",
  ratingSubmit: "cmd.core.rating.submit",
  artifactGet: "cmd.core.artifact.get",
  artifactDownloadPrepare: "cmd.core.artifact.download.prepare",
  messagesList: "cmd.core.messages.list",
  agentMessage: "cmd.core.agent.message",
  agentCancel: "cmd.core.agent.cancel",
  chatNew: "cmd.core.chat.new",
} as const;

// ─── Shared types ────────────────────────────────────────────────

export interface ExpansionCandidate {
  category: string;
  product: string;
  snippet: string;
}

export interface SearchRunHistoryItem {
  runId: string;
  platform: string;
  platformUserId: string;
  accountUserId?: string | null;
  chatId?: number | null;
  query: string;
  why?: string | null;
  strict: boolean;
  requirePrices: boolean;
  status: string;
  error?: string | null;
  jobId?: string | null;
  csvFileId?: string | null;
  pdfFileId?: string | null;
  pdfFilename?: string | null;
  /** Present once the run completed (lead created server-side). */
  leadUuid?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  /** Live progress snapshot from parser — null for terminal states. */
  progress?: JobProgress | null;
}

export type SearchExecutionStatus =
  | "idle" | "starting" | "running" | "artifacts_ready"
  | "finalizing" | "completed" | "failed" | "cancelled"
  | "cancelled_local" | "cancel_failed" | "cancel_error"
  | "timed_out" | "aborted" | "not_found" | "unknown";

export interface JobProgress {
  processed: number;
  total?: number | null;
  currentSource?: string | null;
  stage: string;
}

export interface SearchExecutionSnapshot {
  actorKey: string;
  status: SearchExecutionStatus;
  startedAt: string;
  updatedAt: string;
  runId?: string | null;
  jobId?: string | null;
  query?: string;
  why?: string;
  strict?: boolean;
  requirePrices?: boolean;
  csvFileId?: string | null;
  pdfFileId?: string | null;
  pdfFilename?: string | null;
  leadUuid?: string;
  managerMessageLink?: string | null;
  error?: string | null;
  progress?: JobProgress | null;
}

// ─── Search Prepare ──────────────────────────────────────────────

export interface SearchPrepareRequest {
  rawQuery: string;
  /** Desired expansion candidate count (optional). Omitted = parser legacy 4. */
  limit?: number;
}

export type SearchPrepareResponse =
  | { kind: "expansion_options_available"; rawQuery: string; sanitizedQuery: string; expansion: { query: string; identifiedAs?: string; candidates: ExpansionCandidate[] } }
  | { kind: "validation_failure" | "pre_query_denial" | "expansion_unavailable"; rawQuery?: string; error?: string };

// ─── Search Select Expansion ─────────────────────────────────────

export interface SearchSelectExpansionRequest {
  originalQuery: string;
  candidates: ExpansionCandidate[];
  selectionIndex: number;
}

export type SearchSelectExpansionResponse =
  | { kind: "ready_for_why"; selection: { selectedQuery: string; selectionIndex: number; isOriginalQuery: boolean } }
  | { kind: "invalid_selection" | "confirmation_required"; error?: string };

// ─── Search Start ─────────────────────────────────────────────────

export interface SearchStartRequest {
  query: string;
  why?: string;
  strict: boolean;
  requirePrices: boolean;
  selectedExpansion?: ExpansionCandidate;
}

export interface SearchStartResponse {
  success: boolean;
  status: string;
  actorKey: string;
  runId?: string | null;
  jobId?: string | null;
  error?: string;
}

// ─── Search Status ────────────────────────────────────────────────

export interface SearchStatusRequest {
  searchId?: string;
}

export type SearchStatusResponse = SearchExecutionSnapshot;

// ─── Search Finalize ──────────────────────────────────────────────

export interface SearchFinalizeRequest {
  query: string;
  why?: string;
  csvFileId?: string | null;
  pdfFileId?: string | null;
}

export interface SearchFinalizeResponse {
  success: boolean;
  status: string;
  actorKey: string;
  runId?: string | null;
  leadUuid?: string;
  shouldAskForRating: boolean;
  error?: string;
}

// ─── Search Cancel ────────────────────────────────────────────────

export interface SearchCancelRequest {
  searchId?: string;
}

export type SearchCancelResponse = SearchExecutionSnapshot;

// ─── Search History ───────────────────────────────────────────────

export interface SearchHistoryRequest {
  limit?: number;
}

export type SearchHistoryResponse = SearchRunHistoryItem[];

// ─── Manager Help ─────────────────────────────────────────────────

export interface ManagerHelpRequest {
  requestText: string;
  managerHelpOption?: string;
  purchaseTimingOption?: string;
  phone: string;
}

export interface ManagerHelpResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// ─── Rating ───────────────────────────────────────────────────────

export interface RatingRequest {
  rate: number;
  runId?: string;
  productQuery?: string;
  why?: string;
}

export interface RatingResponse {
  success: boolean;
  error?: string;
}

// ─── Messages List ────────────────────────────────────────────────

export interface MessagesListRequest {
  channel?: string;
  limit?: number;
  before?: string;
}

export interface MessageItem {
  id: number;
  channel: string;
  role: "user" | "assistant" | "system" | "agent";
  text: string;
  meta: string | null;
  timestamp: string;
  threadId: number | null;
}

/** Lightweight thread metadata: agent threads drive "Новый чат" boundaries,
 *  static threads hold adapter visibility copies. */
export interface ThreadMeta {
  id: number;
  kind: "agent" | "static";
  createdAt: string;
}

export interface MessagesListResponse {
  messages: MessageItem[];
  threads: ThreadMeta[];
  currentThreadId: number | null;
}

// ─── Chat New (start a fresh agent thread) ─────────────────────────

export interface ChatNewResponse {
  threadId: number;
  createdAt: string;
}

// ─── Agent Message ────────────────────────────────────────────────

export interface ChatFile {
  /** Present for agent-produced files (downloadable artifact). */
  artifactId?: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  caption?: string;
  /** Workspace-relative path (`uploads/<name>`) for user uploads. */
  path?: string;
}

export interface AgentMessageRequest {
  text: string;
  /** Target agent thread; absent → Core resolves latest agent thread (or legacy NULL). */
  threadId?: number | null;
  /**
   * Host-page prefill (e.g. WP product info from `[bot_suite_v2 prefill="auto"]`).
   * botCore appends this to the system prompt on every turn so the agent
   * ALWAYS has the host context from turn 0 — no need for the user to type
   * it. Empty / undefined → no prefill.
   */
  hostContext?: string;
  /** Files already written into the workspace via /api/agent/upload. */
  files?: ChatFile[];
}

export interface ChatAction {
  kind: "new_order" | "open_orders" | "open_about" | "support" | "send" | "manager_help";
  label: string;
  payload?: Record<string, unknown>;
}

export interface AgentMessageResponse {
  text: string;
  actions?: ChatAction[];
  files?: ChatFile[];
}

// ─── Agent Cancel ──────────────────────────────────────────────────

export interface AgentCancelResponse {
  success: boolean;
  error?: string;
}
